import { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Button from 'components/gruenland/atoms/button/Button';
import { Container } from 'components/gruenland/atoms/container/Container';
import Drawer from 'components/gruenland/atoms/drawer/Drawer';
import { HtmlField } from 'components/gruenland/atoms/html/HtmlField';
import Link from 'components/gruenland/atoms/link';
import Typography from 'components/gruenland/atoms/typography';
import { Close } from 'components/gruenland/icons/Close';
import Breadcrumb from 'components/gruenland/molecules/breadcrumb/Breadcrumb';
import Features from 'components/gruenland/molecules/product-details/Features';
import Technologies from 'components/gruenland/molecules/product-details/Technologies';
import StarRating from 'components/gruenland/molecules/ratings/StarRating';
import WishlistButton from 'components/gruenland/molecules/wishlist/WishlistButton';
import StoreLocatorModal from 'components/gruenland/pages/store-locator/StoreLocatorModal';
import useAverageRatings from 'components/yggdrasil/organisms/feedaty/hooks/useAverageRatings';
import Reviews from 'components/yggdrasil/organisms/feedaty/Reviews';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { useFormat } from 'helpers/hooks/useFormat';
import useShopable from 'helpers/hooks/useShopable';
import { sendViewItemEvent } from 'helpers/utils/ga4';
import { TYPOGRAPHY_PDP_LINKS, TYPOGRAPHY_PDP_REVEW_LABEL } from 'lib/typography';
import { product360Sprite, productDetailPageImages } from 'lib/wardaImages';
import { useCurrentCategory } from 'providers/category';
import { Category } from 'types/entity/category';
import { Variant } from 'types/entity/product';
import { LineItem, Wishlist } from 'types/entity/wishlist';
import { useCart } from 'frontastic/hooks';
import Gallery from '../gallery';
import ProductInformation from './components/product-information';
import SharePicker from './SharePicker';
import { UIProduct } from './types';
import MarketButton from '../market-button/MarketButton';
import { MarketProvider } from 'context/market';

export interface ProductDetailsProps {
  product: UIProduct;
  variant: Variant;
  category?: Category;
  wishlist?: Wishlist;
  onChangeVariant: (sku: string) => void;
  removeLineItem?: (item: LineItem) => Promise<void>;
  addToWishlist?: (lineItem: LineItem, count: number) => Promise<void>;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  variant,
  category,
  wishlist,
  onChangeVariant,
  removeLineItem,
  addToWishlist,
}) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [quantity, setQuantity] = useState<number>(variant.isOnStock ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  const { show } = useAddToCartOverlay();

  const { addItem } = useCart();

  const handleAddToCart = useCallback(async () => {
    setLoading(true);

    addItem(product, variant, quantity).then(() => {
      setLoading(false);

      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        setQuantity(1);
      }, 1000);

      show(product, variant, quantity);
    });
  }, [addItem, product, variant, quantity, show]);

  const productToWishlistLineItem = useMemo<LineItem>(() => {
    return {
      lineItemId: product?.productId ?? '',
      productId: product?.productId,
      productKey: product?.productKey,
      productRef: product?.productRef,
      name: product?.name,
      count: 1,
      variant: variant,
      addedAt: new Date(),
      _url: product?._url,
    };
  }, [product, variant]);

  const image360 = product360Sprite(variant.images);
  const images = productDetailPageImages(variant.images);

  const shopable = useShopable();

  const [isSpedResiDrawerOpen, setIsSpedResiDrawerOpen] = useState(false);

  const { setCategoryKeys } = useCurrentCategory();

  useEffect(() => {
    setCategoryKeys(
      product.categories?.map((x) => x.categoryKey).filter((key): key is string => key !== undefined) ?? [],
    );
  }, [setCategoryKeys, product.categories]);

  const averageRatings = useAverageRatings(product.productKey);

  useEffect(() => {
    sendViewItemEvent(product, variant);
  }, [product, variant]);

  const [showStores, setShowStores] = useState(false);
  const showStoresModal = useCallback(() => setShowStores(true), []);
  const closeStoresModal = useCallback(() => setShowStores(false), []);

  return (
    <>
      <Container>
        <div className="pb-8 pt-5 lg:grid lg:grid-cols-12 lg:items-start lg:pt-6">
          {category && (
            <Breadcrumb className="col-span-12 mb-6 hidden w-fit lg:block">
              <Link key={category.categoryId} link={category._url}>
                {category.name}
              </Link>
              <Typography key={product.slug}>{product.name}</Typography>
            </Breadcrumb>
          )}

          <div className="lg:col-span-6 lg:pr-[60px]">
            <Gallery images={images} image360={image360} />
          </div>

          <div className="sticky top-[80px] mt-20 lg:col-span-6 lg:mt-6">
            {shopable && !variant.isOnStock && (
              <p className="font-medium text-red-500">
                {formatMessage({ id: 'badges.outOfStock', defaultMessage: 'Non disponibile' })}
              </p>
            )}

            <ProductInformation product={product} variant={variant} onChangeVariant={onChangeVariant} />
            <div className="mt-5 flex flex-col items-center gap-4 lg:flex-row">
              <div className="w-full">
                {shopable ? (
                  <Button
                    className="h-[64px] w-full"
                    variant="buttonPrimaryBlack"
                    onClick={handleAddToCart}
                    loading={loading}
                    added={added}
                    disabled={!variant.isOnStock}
                  >
                    {variant.isOnStock
                      ? formatMessage({ id: 'cart.add', defaultMessage: 'Aggiungi al carrello' })
                      : formatMessage({ id: 'common.unavailable', defaultMessage: 'Non disponibile' })}
                  </Button>
                ) : (
                  <MarketProvider>
                    <MarketButton className="w-full">
                      <div className="flex h-[64px] w-full cursor-pointer items-center justify-center rounded-full bg-black px-6 py-3 text-[18px]/[22.68px] font-semibold text-white transition-colors hover:bg-gray-800">
                        {formatMessage({ id: 'i18n.selectCountry', defaultMessage: 'Seleziona il paese' })}
                      </div>
                    </MarketButton>
                  </MarketProvider>
                )}
              </div>
              <div className="flex gap-4">
                <SharePicker text={product.name} />
                <WishlistButton
                  lineItem={productToWishlistLineItem}
                  data={wishlist}
                  removeFromWishlist={removeLineItem}
                  addToWishlist={addToWishlist}
                  className="flex size-[64px] items-center justify-center rounded-full ring-2 ring-inset ring-black"
                  icoWidth={22}
                />
              </div>
            </div>
            <div className="mt-10 hidden gap-10 lg:flex">
              <a className={classNames(TYPOGRAPHY_PDP_LINKS, 'border-b border-neutrals-black')} href="#pdpFeatures">
                {formatMessage({ id: 'product.features', defaultMessage: 'Caratteristiche' })}
              </a>
              <a className={classNames(TYPOGRAPHY_PDP_LINKS, 'border-b border-neutrals-black')} href="#pdpTechnologies">
                {formatMessage({ id: 'product.technologies', defaultMessage: 'Tecnologie' })}
              </a>
            </div>
            {averageRatings?.rating > 0 && (
              <div className="mt-10 flex items-center gap-4">
                <StarRating rating={averageRatings.rating} />
                <span className={TYPOGRAPHY_PDP_REVEW_LABEL}>
                  {formatMessage({
                    id: 'feedaty.countReviews',
                    defaultMessage: '({count}) recensioni',
                    values: { count: averageRatings.count },
                  })}
                </span>
              </div>
            )}
            <HtmlField html={product?.description} className="mt-5" />
            <p className="mt-10">
              {variant?.attributes?.structure} / {product?.productKey} / {variant?.attributes?.ean}
            </p>
            <div className="mt-10 flex flex-col items-start gap-5 lg:flex-row lg:gap-10">
              {product.productKey && (
                <>
                  <button
                    className={classNames(TYPOGRAPHY_PDP_LINKS, 'border-b border-neutrals-black')}
                    type="button"
                    onClick={showStoresModal}
                  >
                    {formatMessage({ id: 'pdp.buyInStore', defaultMessage: 'Acquista in negozio' })}
                  </button>
                  <StoreLocatorModal open={showStores} onClose={closeStoresModal} productId={product.productKey} />
                </>
              )}
              <Link
                link="/tecnologie"
                className={classNames(TYPOGRAPHY_PDP_LINKS, 'border-b border-neutrals-black lg:hidden')}
              >
                {formatMessage({ id: 'pdp.technologies', defaultMessage: 'Tecnologie' })}
              </Link>
              <button
                onClick={() => setIsSpedResiDrawerOpen(true)}
                className={classNames(TYPOGRAPHY_PDP_LINKS, 'border-b border-neutrals-black')}
              >
                {formatMessage({ id: 'pdp.shippingAndReturns', defaultMessage: 'Spedizione e resi' })}
              </button>
              <Drawer
                isOpen={isSpedResiDrawerOpen}
                onClose={() => setIsSpedResiDrawerOpen(false)}
                direction="right"
                className="w-[90%] p-[60px] lg:max-w-[50%]"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-[40px]/[48px] font-semibold">
                    {formatMessage({ id: 'plp.resi.title', defaultMessage: 'Resi e spedizioni' })}
                  </h3>
                  <button onClick={() => setIsSpedResiDrawerOpen(false)}>
                    <Close />
                  </button>
                </div>
                <div className="mt-8">
                  <p className="font-semibold">
                    {formatMessage({ id: 'plp.resi.spedizione', defaultMessage: 'Spedizione' })}
                  </p>
                  <p>
                    {formatMessage({
                      id: 'plp.resi.spedizione.text',
                      defaultMessage: 'Consegna tramite corriere espresso',
                    })}
                  </p>
                  <p className="mt-10 font-semibold">
                    {formatMessage({ id: 'plp.resi.resi', defaultMessage: 'Resi' })}
                  </p>
                  <p>{formatMessage({ id: 'plp.resi.resi.text', defaultMessage: 'Reso gratuito entro 14 giorni' })}</p>
                  <p className="mt-10 font-semibold">
                    {formatMessage({ id: 'plp.resi.garanzia', defaultMessage: 'Garanzia' })}
                  </p>
                  <p>
                    {formatMessage({
                      id: 'plp.resi.garanzia.text',
                      defaultMessage: '2 anni di garanzia per difetti di fabbricazione',
                    })}
                  </p>
                </div>
              </Drawer>
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <Features items={variant?.attributes} className="flex flex-col items-center bg-white py-20" />
        <Technologies
          technologies={variant?.attributes?.technologies}
          className="flex flex-col items-center bg-white py-20"
        />
      </Container>
      <Reviews productKey={product.productKey} />
    </>
  );
};

export default ProductDetails;
